import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabView, TabPanel } from 'primereact/tabview';
import { Tooltip } from 'primereact/tooltip';
import { Chart } from 'primereact/chart';
import { Chart as ChartJS, BarController, BarElement, CategoryScale, LinearScale, Tooltip as ChartJSTooltip, Legend as ChartJSLegend } from 'chart.js';
import moment from 'moment';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    selectSalesData,
    selectSummary,
    selectSummaryCards,
    selectSummaryModeTooltips,
    selectPaymentModes,
    selectLoading,
    selectFromDate,
    selectToDate,
    selectAppliedFromDate,
    selectAppliedToDate,
    selectAppliedBranch,
    selectSelectedBranch,
    selectError,
    selectOrgName,
    selectBranchTitle
} from '../../store/slices/salesReportsSlice';
import { getHourlySalesReport, getSalesReportData, getBranches, filterBillsList, formatDateForApi } from '../../utilities/api';
import useUser from '../../hooks/useUser';
import ReportSummaryWithChart from './ReportSummaryWithChart';
import { exportStyledExcel } from '../../utilities/excelExport';
import { buildBranchAnalysisRows } from '../../utilities/branchAnalysis';
import './billreports.css';

ChartJS.register(BarController, BarElement, CategoryScale, LinearScale, ChartJSTooltip, ChartJSLegend);

// Validated categorical palette (fixed order — never cycled by rank). Used for the Payment
// Split chart's stacked segments and the matching hover-popup dots, so a mode keeps the same
// color everywhere regardless of which branch/amount ranks it by.
const CATEGORICAL_PALETTE = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];

// Kept in place (not deleted) in case the payment-type cards + donut are wanted back —
// Branch Analysis's own per-branch Chart View replaced its job.
const SALES_SUMMARY_ENABLED = false;

// Branch Analysis chart view: one series -> one color (dataviz color-formula, "sequential
// magnitude" job) — Total Sales and Total Bills are different scales, so they're two
// single-axis charts (small multiples), never one dual-axis chart. Categorical slots 1/3.
const BRANCH_SALES_CHART_COLOR = '#2a78d6';
const BRANCH_BILLS_CHART_COLOR = '#1baf7a';
const CHART_GRID_COLOR = '#e1e0d9';
const CHART_AXIS_TICK_COLOR = '#898781';
const CHART_SURFACE_COLOR = '#fcfcfb';

// PrimeReact's table cells (th/td, header/body/footer alike) are flex boxes, so
// positioning is actually driven by justifyContent — a bare textAlign only affects
// the anonymous inline box's own (already content-sized) text and does nothing to
// where that box sits in the cell. Every aligned cell style needs both. Shared with
// Sales Reporta so both pages' tables/footers align and weight the same way.
const TEXT_ALIGN_TO_JUSTIFY = { left: 'flex-start', right: 'flex-end', center: 'center' };
const alignStyle = (textAlign, extra) => ({ textAlign, justifyContent: TEXT_ALIGN_TO_JUSTIFY[textAlign], ...extra });
const headerAlignStyle = (style) => (style?.textAlign ? { textAlign: style.textAlign, justifyContent: TEXT_ALIGN_TO_JUSTIFY[style.textAlign] } : undefined);
/** Every footer value shares one weight (700) and size — a larger inline fontSize on just the
 * Grand Total cell used to grow that cell past the fixed row height and overlap the row above it,
 * so emphasis comes from boldness alone, kept uniform with the rest of the footer row. */
const footerCellStyle = (textAlign, { isGrandTotal = false } = {}) => alignStyle(textAlign, { fontWeight: 700, color: isGrandTotal ? 'var(--primary-color)' : undefined });

const SalesReportsContent = ({ isManagerView = false }) => {
    // Redux selectors
    const salesData = useSelector(selectSalesData);
    const summary = useSelector(selectSummary);
    const summaryCards = useSelector(selectSummaryCards);
    const summaryModeTooltips = useSelector(selectSummaryModeTooltips);
    const paymentModes = useSelector(selectPaymentModes);
    const loading = useSelector(selectLoading);
    const fromDate = useSelector(selectFromDate);
    const toDate = useSelector(selectToDate);
    const selectedBranch = useSelector(selectSelectedBranch);
    // Branch Analysis fetches off these instead of the live filters above — they only
    // change on Apply/Search, so picking a date/branch alone doesn't trigger a fetch.
    const appliedFromDate = useSelector(selectAppliedFromDate);
    const appliedToDate = useSelector(selectAppliedToDate);
    const appliedBranch = useSelector(selectAppliedBranch);
    const error = useSelector(selectError);
    const orgNameRaw = useSelector(selectOrgName);
    const branchTitleRaw = useSelector(selectBranchTitle);

    // Same org_name / branch_title precedence as Bill Report's export header. Both are
    // trimmed first — a branch_title that's DB whitespace-only (truthy but blank) would
    // otherwise still pass the "set and different" check and print a dangling " / " with
    // nothing after it.
    const orgNameTrimmed = (orgNameRaw || '').trim();
    const branchTitleTrimmed = (branchTitleRaw || '').trim();
    const organizationName = orgNameTrimmed && branchTitleTrimmed && orgNameTrimmed !== branchTitleTrimmed ? `${orgNameTrimmed} / ${branchTitleTrimmed}` : orgNameTrimmed || branchTitleTrimmed || '';

    // Get role from localStorage
    const { userRole } = useUser();
    const rolename = userRole || localStorage.getItem('role') || '';
    const isCEO = rolename.toUpperCase() === 'CEO';
    const isAdmin = rolename.toUpperCase() === 'ADMIN';
    const canExport = !isManagerView && (isCEO || isAdmin);

    // Local state
    const [filteredSalesData, setFilteredSalesData] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [hourlyGlobalFilter, setHourlyGlobalFilter] = useState('');
    const [activeReportTab, setActiveReportTab] = useState(0);
    const [hourlyData, setHourlyData] = useState(null);
    const [loadingHourly, setLoadingHourly] = useState(false);
    const [expandedHourlyRows, setExpandedHourlyRows] = useState({});
    const [branches, setBranches] = useState([]);
    const [branchBillRows, setBranchBillRows] = useState([]);
    const [branchBillLoading, setBranchBillLoading] = useState(false);
    const [branchModeAmounts, setBranchModeAmounts] = useState({});
    const [branchModeLoading, setBranchModeLoading] = useState(false);
    const [branchAnalysisView, setBranchAnalysisView] = useState('table');

    // Format date. Backend sends dates as DD-MM-YYYY (occasionally YYYY-MM-DD) — parse
    // explicitly with those formats rather than moment's ambiguous auto-parse, which
    // misreads "11-09-2026" as MM-DD-YYYY and swaps day/month.
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const parsed = moment(dateString, ['DD-MM-YYYY', 'YYYY-MM-DD'], true);
        return parsed.isValid() ? parsed.format('DD-MM-YYYY') : dateString;
    };

    // Format currency
    const formatCurrency = (amount) => {
        const num = parseFloat(amount || 0);
        return num.toLocaleString('en-IN', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2
        });
    };

    const isStrictNumericValue = (value) => /^-?\d+(\.\d+)?$/.test(String(value ?? '').trim());

    const isDateLikeField = (field) => {
        const fieldName = String(field || '').toLowerCase();
        return fieldName.includes('date') || fieldName.includes('time');
    };

    // Narrower than isDateLikeField — only actual date columns (not "time") get reformatted to DD-MM-YYYY.
    const isDateOnlyField = (field) => String(field || '').toLowerCase().includes('date');

    // A "_Qty"/"_qty" field (NC_Qty, Discount_Qty, bill counts) is a count, not money — no ₹ prefix.
    const isQtyField = (field) => String(field || '').toLowerCase().includes('qty');

    const shouldFormatAsCurrency = (field, value) => {
        if (value === null || value === undefined || value === '') return false;
        if (isDateLikeField(field) || isQtyField(field)) return false;
        return isStrictNumericValue(value);
    };

    // Report payload (same as header: from_date, to_date, branch_id)
    const reportPayload = useCallback(() => {
        const branchId = selectedBranch?.branch_id || 'All';
        const payload = {
            from_date: fromDate ? formatDateForApi(fromDate) : '',
            to_date: toDate ? formatDateForApi(toDate) : '',
            branch_id: branchId
        };
        if (String(branchId).toLowerCase() === 'all') {
            const clientId = localStorage.getItem('clientid');
            if (clientId) payload.client_id = clientId;
        }
        return payload;
    }, [fromDate, toDate, selectedBranch]);

    // Reset hourly cache when filters change
    useEffect(() => {
        setHourlyData(null);
    }, [fromDate, toDate, selectedBranch?.branch_id]);

    // Fetch hourly sales when Hourly tab is selected
    useEffect(() => {
        if (activeReportTab !== 1 || hourlyData !== null || !fromDate || !toDate) return;
        const fetchHourly = async () => {
            setLoadingHourly(true);
            try {
                const res = await getHourlySalesReport(reportPayload());
                if (res?.status === 'success' && res?.data) setHourlyData(res.data);
                else setHourlyData({});
            } catch {
                setHourlyData({});
            } finally {
                setLoadingHourly(false);
            }
        };
        fetchHourly();
    }, [activeReportTab, fromDate, toDate, hourlyData, reportPayload]);

    // Branch list for the "All Branches" payment-split fetch below (Manager never
    // sees "All", so this is only actually used for Admin/CEO).
    useEffect(() => {
        const clientId = localStorage.getItem('clientid');
        if (!clientId) return;
        getBranches(clientId)
            .then((list) => setBranches(Array.isArray(list) ? list : []))
            .catch(() => setBranches([]));
    }, []);

    // Derived from the applied branch (not the live dropdown selection) — this only
    // drives the Branch Analysis payment-split fetch below, which itself is gated on
    // the applied filters.
    const isAllBranchesSelected = !appliedBranch || String(appliedBranch?.branch_id ?? '').toLowerCase() === 'all';

    // Branch Analysis — bill-level side: one filterBillsList call (same endpoint Bill
    // Reports uses) gets invoice_no/status/final_mop/discount/total_paid_amount for
    // every bill in range, which buildBranchAnalysisRows groups per branch into
    // Invoice No range + gap detection, Cancelled/NC/Credit/Discount split, Total
    // Sales and Avg Bill Value.
    //
    // date_field: 'payment' (Bill Reports' own Branch Analysis omits this and stays on
    // invoice_date) — this page's other two totals (Sales by date range, Hourly sales)
    // are both keyed off payment_date, so Branch Analysis has to match or its grand
    // total won't reconcile with theirs for the same date range. Deliberately scoped to
    // just this fetch: a bill only gets a payment record once it's actually paid, so
    // payment_date filtering would silently drop still-open bills from Bill
    // Reports/Credit Reports/Cashier Sales, which all share this same endpoint.
    useEffect(() => {
        if (!appliedFromDate || !appliedToDate) {
            setBranchBillRows([]);
            return;
        }
        let ignore = false;
        setBranchBillLoading(true);
        const clientId = localStorage.getItem('clientid');
        const payload = {
            from_date: formatDateForApi(appliedFromDate),
            to_date: formatDateForApi(appliedToDate),
            branch_id: appliedBranch?.branch_id || 'All',
            status: '',
            date_field: 'payment'
        };
        if (clientId) payload.client_id = clientId;
        filterBillsList(payload)
            .then((rows) => {
                if (!ignore) setBranchBillRows(Array.isArray(rows) ? rows : []);
            })
            .catch(() => {
                if (!ignore) setBranchBillRows([]);
            })
            .finally(() => {
                if (!ignore) setBranchBillLoading(false);
            });
        return () => {
            ignore = true;
        };
    }, [appliedFromDate, appliedToDate, appliedBranch]);

    /** Actual-sales amount per payment mode (Cash/Card/Paytm/...) from a raw payment_bydaterannge `summary.actual_sales` object — the true payment-type split (payments_details supports a bill being paid via more than one mode, unlike final_mop). */
    const extractModeAmounts = (rawSummary) => {
        const actual = rawSummary?.actual_sales && typeof rawSummary.actual_sales === 'object' ? rawSummary.actual_sales : {};
        const out = {};
        Object.entries(actual).forEach(([key, value]) => {
            if (key.toLowerCase() === 'total') return;
            out[key] = Number(value || 0);
        });
        return out;
    };

    // Branch Analysis — payment-type split: single branch selected reads it straight
    // off the already-loaded Redux `summary` (Total_<mode> keys); "All Branches" has
    // no branch dimension in that combined response (GROUP BY payment_date only), so
    // each branch's own summary is fetched in parallel instead (bypasses Redux, same
    // pattern as the Hourly/User-wise tabs above).
    useEffect(() => {
        if (!appliedFromDate || !appliedToDate) {
            setBranchModeAmounts({});
            return;
        }

        if (!isAllBranchesSelected) {
            if (!summary || !Object.keys(summary).length) {
                setBranchModeAmounts({});
                return;
            }
            const modeAmounts = {};
            paymentModes.forEach((mode) => {
                modeAmounts[mode] = Number(summary[`Total_${mode}`] ?? 0);
            });
            setBranchModeAmounts({ [String(appliedBranch.branch_id)]: modeAmounts });
            return;
        }

        const realBranches = branches.filter((b) => String(b.branch_id).toLowerCase() !== 'all');
        if (!realBranches.length) {
            setBranchModeAmounts({});
            return;
        }

        let ignore = false;
        setBranchModeLoading(true);
        const clientId = localStorage.getItem('clientid') || '';
        const rolename = localStorage.getItem('role') || '';

        Promise.all(
            realBranches.map((b) =>
                getSalesReportData({
                    from_date: formatDateForApi(appliedFromDate),
                    to_date: formatDateForApi(appliedToDate),
                    branch_id: b.branch_id,
                    rolename,
                    clientname: clientId,
                    client_id: clientId
                })
                    .then((res) => [String(b.branch_id), extractModeAmounts(res?.summary || {})])
                    .catch(() => [String(b.branch_id), {}])
            )
        ).then((entries) => {
            if (ignore) return;
            setBranchModeAmounts(Object.fromEntries(entries));
            setBranchModeLoading(false);
        });

        return () => {
            ignore = true;
        };
    }, [isAllBranchesSelected, appliedBranch, appliedFromDate, appliedToDate, branches, summary, paymentModes]);

    const branchAnalysisRows = useMemo(
        () =>
            buildBranchAnalysisRows(branchBillRows).map((row) => ({
                ...row,
                modeAmounts: branchModeAmounts[row.branchId] || {}
            })),
        [branchBillRows, branchModeAmounts]
    );
    const branchAnalysisLoading = branchBillLoading || branchModeLoading;

    // Update filtered data when salesData or global search changes
    useEffect(() => {
        if (!globalFilter) {
            setFilteredSalesData(salesData);
            return;
        }
        const searchTerm = globalFilter.toLowerCase();
        const filtered = salesData.filter((item) => {
            return Object.keys(item).some((key) => {
                if (key === '_modeTooltips') return false;
                const value = item[key];
                if (value === null || value === undefined) return false;
                return String(value).toLowerCase().includes(searchTerm);
            });
        });
        setFilteredSalesData(filtered);
    }, [globalFilter, salesData]);

    // Format key as header (e.g. payment_date -> Payment Date, Total_CASH -> Total Cash)
    const formatKeyAsHeader = (key) => {
        if (!key) return '';
        return key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    };

    // Same as above but drops a leading "Total" (Total_CASH -> Cash) — the summary
    // tiles/table already group these under a payment-mode breakdown, so repeating
    // "Total" on every row is redundant and just eats the space the label needs.
    const formatSummaryLabel = (key) => {
        if (!key) return '';
        return key.replace(/^total_/i, '').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    };

    // Summary card style from key text (CASH, CARD, PAYTM, etc. are dynamic).
    // Colors are the validated categorical set (see dataviz palette) so payment
    // modes stay distinguishable for color-vision-deficient viewers, and the same
    // hex drives both the stat tile accent and its matching donut slice.
    const getSummaryCardStyle = (key) => {
        const k = (key || '').toUpperCase();
        // No hero-tile promotion here — everything renders as one ordered list
        // (payment modes, then Actual/NC/Expected/Cancelled/Total), matching the table.
        const isHighlight = false;
        const styles = {
            CASH: { bg: 'linear-gradient(135deg, #1baf7a 0%, #0f766e 100%)', icon: 'pi-wallet' },
            CARD: { bg: 'linear-gradient(135deg, #2a78d6 0%, #184f95 100%)', icon: 'pi-credit-card' },
            PAYTM: { bg: 'linear-gradient(135deg, #eda100 0%, #c98500 100%)', icon: 'pi-mobile' },
            UPI: { bg: 'linear-gradient(135deg, #eda100 0%, #c98500 100%)', icon: 'pi-mobile' },
            PHONEPE: { bg: 'linear-gradient(135deg, #eda100 0%, #c98500 100%)', icon: 'pi-mobile' },
            CREDIT: { bg: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)', icon: 'pi-clock' },
            // Checked before NC — "CANCELLED" contains the substring "NC", so it
            // would otherwise be mis-styled as the NC (no-charge) category.
            CANCELLED: { bg: 'linear-gradient(135deg, #94a3b8 0%, #475569 100%)', icon: 'pi-ban' },
            NC: { bg: 'linear-gradient(135deg, #e34948 0%, #b91c1c 100%)', icon: 'pi-tag' },
            DISCOUNT: { bg: 'linear-gradient(135deg, #eda100 0%, #c98500 100%)', icon: 'pi-percentage' },
            ACTUAL: { bg: 'linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%)', icon: 'pi-chart-bar' },
            EXPECTED: { bg: 'linear-gradient(135deg, #0d9488 0%, #115e59 100%)', icon: 'pi-chart-line' },
            // Exact match on the grand-total key (Expected + Cancelled) — checked
            // last since "TOTAL_TOTAL" won't accidentally collide with the others.
            // Gold/amber so it stays visually distinct from Expected Sales' teal.
            TOTAL_TOTAL: { bg: 'linear-gradient(135deg, #ca8a04 0%, #854d0e 100%)', icon: 'pi-money-bill' }
        };
        const fallbackPalette = [
            { bg: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)', icon: 'pi-wallet' },
            { bg: 'linear-gradient(135deg, #e87ba4 0%, #be3a72 100%)', icon: 'pi-mobile' },
            { bg: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', icon: 'pi-dollar' }
        ];
        for (const [match, style] of Object.entries(styles)) {
            if (k.includes(match)) return { bg: style.bg, icon: style.icon, isHighlight };
        }
        const hash = (key || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
        const fallback = fallbackPalette[Math.abs(hash) % fallbackPalette.length];
        return { bg: fallback.bg, icon: fallback.icon, isHighlight };
    };

    // Actual/Expected/Total are sums of the payment-mode rows (and of each other),
    // so they'd double-count if mixed into the donut's part-to-whole breakdown.
    const excludeSalesTotalFromChart = (key) => {
        const k = (key || '').toUpperCase();
        return k === 'TOTAL_ACTUAL_SALES' || k === 'TOTAL_EXPECTED_SALES' || k === 'TOTAL_TOTAL';
    };

    // Actual Sales, Expected Sales and the grand Total headings get bold/colored
    // emphasis text in the summary (Actual/Expected keep their own accent color;
    // Total keeps its gold accent — see styles above).
    const SALES_EMPHASIZE_KEYS = ['TOTAL_ACTUAL_SALES', 'TOTAL_EXPECTED_SALES', 'TOTAL_TOTAL'];
    const emphasizeSalesTotal = (key) => SALES_EMPHASIZE_KEYS.includes((key || '').toUpperCase());

    // These are fixed table columns (Actual Sales, Expected Sales, Cancelled, Total) —
    // always show them in the summary even if the aggregate is 0 for the selected range,
    // unlike an optional payment mode (Paytm, etc.) that's fine to hide. NC and Discount
    // are optional breakdowns — they only show when the filtered data has a non-zero
    // value for them (handled by ReportSummaryWithChart's own zero-hiding).
    const SALES_ALWAYS_SHOW_KEYS = ['TOTAL_ACTUAL_SALES', 'TOTAL_EXPECTED_SALES', 'TOTAL_CANCELLED', 'TOTAL_TOTAL'];
    const alwaysShowSalesField = (key) => SALES_ALWAYS_SHOW_KEYS.includes((key || '').toUpperCase());

    // Same NC/Credit/Cancelled hover breakdown as the table's mode cells, but
    // aggregated over the whole selected range (only payment-mode rows get one).
    const getSalesSummaryTooltip = (key) => {
        const mode = paymentModes.find((m) => `Total_${m}`.toUpperCase() === (key || '').toUpperCase());
        if (!mode) return null;
        const t = summaryModeTooltips?.[mode] || {};
        return [`NC: ₹${formatCurrency(t.NC)}`, `Credit: ₹${formatCurrency(t.Credit)}`, `Cancelled: ₹${formatCurrency(t.Cancelled)}`].join('\n');
    };

    // A payment mode with 0 actual sales across every date in the current range/branch
    // is unused for this report — hide its column from the table and exports, same as
    // the summary already hides an all-zero breakdown row.
    const usedPaymentModes = paymentModes.filter((mode) => salesData.some((row) => Number(row[mode]) > 0));
    const unusedPaymentModes = paymentModes.filter((mode) => !usedPaymentModes.includes(mode));

    // NC/NC_Qty and Discount_Amount/Discount_Qty are dynamic keys on every row (from
    // buildSalesReportView) — hide them from the table/exports unless the filtered data
    // actually has a non-zero value for them.
    const hasNC = salesData.some((row) => Number(row.NC) > 0 || Number(row.NC_Qty) > 0);
    const hasDiscount = salesData.some((row) => Number(row.Discount_Amount) > 0 || Number(row.Discount_Qty) > 0);
    const isHiddenTotalKey = (key) => (!hasNC && (key === 'NC' || key === 'NC_Qty')) || (!hasDiscount && (key === 'Discount_Amount' || key === 'Discount_Qty'));

    /** Colorful floating breakdown popup, same as Bill Reports' — { type: 'bills'|'sales'|'split'|'invoice', row, top, left }. */
    const [branchHover, setBranchHover] = useState(null);
    const showBranchBreakdown = (type, row) => (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setBranchHover({ type, row, top: rect.bottom + 8, left: rect.left });
    };
    const hideBranchBreakdown = () => setBranchHover(null);

    /** Cancelled/NC/Credit/Discount split for the Total Bills / Total Sales hover cards — identical to Bill Reports. */
    const breakdownCategories = (row, value) =>
        [
            { key: 'cancelled', label: 'Cancelled', value: value(row.cancelledCount, row.cancelledAmount) },
            { key: 'nc', label: 'NC', value: value(row.ncCount, row.ncAmount) },
            { key: 'credit', label: 'Credit', value: value(row.creditCount, row.creditAmount) },
            { key: 'discount', label: 'Discount', value: value(row.discountCount, row.discountAmount) }
        ].filter((cat) => cat.value > 0);

    /** Payment-type amounts (Cash/Card/Paytm/...) for the Payment Split hover, biggest first. */
    const modeBreakdown = (row) =>
        Object.entries(row.modeAmounts || {})
            .filter(([, amount]) => amount > 0)
            .sort((a, b) => b[1] - a[1]);

    // Every payment mode gets one fixed color from paymentModes' own (stable) order — not
    // reassigned by rank — so Cash/Card/... stay the same color in the hover dots, the
    // Payment Split chart's stacked segments, and its legend.
    const modeColor = (mode) => {
        const idx = paymentModes.indexOf(mode);
        return CATEGORICAL_PALETTE[(idx >= 0 ? idx : 0) % CATEGORICAL_PALETTE.length];
    };

    const branchAnalysisTotals = branchAnalysisRows.reduce(
        (acc, r) => {
            acc.totalBills += r.totalBills;
            acc.totalSales += r.totalSales;
            return acc;
        },
        { totalBills: 0, totalSales: 0 }
    );

    /** Combined Cash/Card/Paytm/... totals across every branch shown, for the footer's Payment Split hover. */
    const combinedModeAmounts = branchAnalysisRows.reduce((acc, r) => {
        Object.entries(r.modeAmounts || {}).forEach(([mode, amount]) => {
            acc[mode] = (acc[mode] || 0) + amount;
        });
        return acc;
    }, {});
    const combinedModeRow = { branchId: 'ALL', branchName: 'All Branches', modeAmounts: combinedModeAmounts };

    // Export to PDF
    const buildPdfDoc = () => {
        const doc = new jsPDF();
        const centerX = doc.internal.pageSize.getWidth() / 2;
        const rightX = doc.internal.pageSize.getWidth() - 14;
        let yPos = 15;
        if (organizationName) {
            doc.setFontSize(15);
            doc.setFont(undefined, 'bold');
            doc.text(organizationName, centerX, yPos, { align: 'center' });
            yPos += 8;
        }
        doc.setFontSize(13);
        doc.setFont(undefined, 'bold');
        doc.text('Sales Reports', centerX, yPos, { align: 'center' });
        yPos += 8;

        // Add filter info
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        if (fromDate && toDate) {
            doc.text(`Period: ${moment(fromDate).format('DD-MM-YYYY')} to ${moment(toDate).format('DD-MM-YYYY')}`, 14, yPos);
        }
        doc.text(`Generated On: ${moment().format('DD-MM-YYYY HH:mm')}`, rightX, yPos, { align: 'right' });
        yPos += 5;
        if (selectedBranch) {
            doc.text(`Branch: ${selectedBranch.branch_name || selectedBranch}`, 14, yPos);
            yPos += 5;
        }

        const keys = filteredSalesData.length > 0 ? Object.keys(filteredSalesData[0]).filter((k) => k !== '_modeTooltips' && !unusedPaymentModes.includes(k) && !isHiddenTotalKey(k)) : [];
        const headers = keys.map((k) => formatKeyAsHeader(k));

        const tableBody = filteredSalesData.map((item) =>
            keys.map((key) => {
                const value = item[key];
                if (value === null || value === undefined || value === '') return '';
                if (isDateOnlyField(key)) return formatDate(value);
                const valueStr = String(value).trim();
                if (shouldFormatAsCurrency(key, valueStr)) return `Rs.${formatCurrency(Number(valueStr))}`;
                return valueStr;
            })
        );

        // Add summary row: map table keys to summary keys (e.g. Cash -> Total_Actual_Cash)
        if (Object.keys(summary).length > 0 && keys.length > 0) {
            const summaryRow = keys.map((key, idx) => {
                if (idx === 0) return 'TOTAL';
                const val = summary[`Total_${key}`];
                if (val !== undefined && val !== null) {
                    const num = parseFloat(val);
                    return isNaN(num) ? String(val) : `Rs.${formatCurrency(num)}`;
                }
                return '';
            });
            tableBody.push(summaryRow);
        }

        autoTable(doc, {
            startY: yPos + 5,
            head: [headers],
            body: tableBody,
            theme: 'grid',
            styles: { fontSize: 8, lineWidth: 0.1, lineColor: [180, 180, 180] },
            headStyles: { fillColor: [0, 122, 255], textColor: 255, fontStyle: 'bold', halign: 'center' },
            footStyles: { fontStyle: 'bold', fillColor: [240, 240, 240] },
            didParseCell: function (data) {
                // Make summary row bold
                if (data.row.index === tableBody.length - 1 && Object.keys(summary).length > 0) {
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fillColor = [240, 240, 240];
                }
            }
        });

        return doc;
    };

    const exportToPDF = () => {
        if (filteredSalesData.length === 0) return;
        buildPdfDoc().save(`SalesReport_${moment().format('YYYY-MM-DD')}.pdf`);
    };

    const printReport = () => {
        if (filteredSalesData.length === 0) return;
        const doc = buildPdfDoc();
        doc.autoPrint();
        window.open(doc.output('bloburl'), '_blank');
    };

    // Export to Excel
    const exportToExcel = async () => {
        if (filteredSalesData.length === 0) {
            return;
        }

        const keys = Object.keys(filteredSalesData[0]).filter((k) => k !== '_modeTooltips' && !unusedPaymentModes.includes(k) && !isHiddenTotalKey(k));
        const columns = keys.map((key) => ({
            header: formatKeyAsHeader(key),
            getValue: (item) => {
                const value = item[key];
                if (value === null || value === undefined || value === '') return '-';
                if (isDateOnlyField(key)) return formatDate(value);
                return shouldFormatAsCurrency(key, filteredSalesData[0][key]) ? Number(String(value).trim()) : value;
            },
            numeric: shouldFormatAsCurrency(key, filteredSalesData[0][key])
        }));

        // Organization name (highlighted, centered) then Period left / Branch right, then
        // Generated On (right) — same header shape as Bill Report, minus Invoice No (this
        // report is date-aggregated, with no bill-level invoice_no to range over).
        await exportStyledExcel({
            fileName: `SalesReport_${moment().format('YYYY-MM-DD')}.xlsx`,
            sheetName: 'Sales Report',
            title: 'Sales Reports',
            filterLines: [
                organizationName ? { text: organizationName, bold: true, size: 12, center: true, fill: 'FFEDEBFA' } : null,
                {
                    left: fromDate && toDate ? `Period: ${moment(fromDate).format('DD-MM-YYYY')} to ${moment(toDate).format('DD-MM-YYYY')}` : '',
                    right: `Branch: ${selectedBranch?.branch_name || selectedBranch || 'All Branches'}`,
                    bold: true,
                    fill: 'FFEDEBFA'
                },
                { right: `Generated On: ${moment().format('DD-MM-YYYY hh:mm A')}`, bold: true, fill: 'FFEDEBFA' }
            ],
            columns,
            rows: filteredSalesData
        });
    };

    // Dynamic cell body: show value as date or currency based on content
    const cellBodyTemplate = (field) => (rowData) => {
        const value = rowData[field];
        if (value === null || value === undefined || value === '') return '-';
        if (shouldFormatAsCurrency(field, value)) {
            return `₹${formatCurrency(Number(String(value).trim()))}`;
        }
        if (isDateOnlyField(field)) return formatDate(value);
        return String(value);
    };

    // Payment-mode cell (Cash/Card/Paytm/...): shows the actual-sales amount for
    // that mode, with a hover tooltip revealing the NC/Credit/Cancelled amounts
    // rung up in the same mode on that date (those aren't shown as their own columns).
    const modeCellBodyTemplate = (mode) => (rowData) => {
        const value = rowData[mode];
        const t = rowData._modeTooltips?.[mode] || {};
        const tooltip = [`NC: ₹${formatCurrency(t.NC)}`, `Credit: ₹${formatCurrency(t.Credit)}`, `Cancelled: ₹${formatCurrency(t.Cancelled)}`].join('\n');
        return (
            <div className="payment-mode-cell" data-pr-tooltip={tooltip} data-pr-position="top">
                ₹{formatCurrency(value)}
            </div>
        );
    };

    // Fixed column layout: Date, one column per discovered payment mode (Cash/Card/
    // Paytm/...) carrying Actual Sales, then Actual Sales / NC / Expected Sales /
    // Cancelled / Total. Credit is intentionally not its own column (see mode tooltip above).
    const getColumns = () => {
        const source = filteredSalesData.length > 0 ? filteredSalesData : salesData;
        if (source.length === 0) return [];

        const dateColumn = {
            field: 'payment_date',
            header: 'Payment Date',
            sortable: true,
            body: cellBodyTemplate('payment_date'),
            style: alignStyle('left', { minWidth: '120px' })
        };

        const modeColumns = usedPaymentModes.map((mode) => ({
            field: mode,
            header: mode,
            sortable: true,
            body: modeCellBodyTemplate(mode),
            style: alignStyle('right', { minWidth: '120px' })
        }));

        // Qty columns (NC Qty, Discount Qty) are left out of the on-screen table — they're
        // counts, not sales figures, and clutter this row of amount columns. Still included
        // in Excel/PDF/Print, which derive their own columns straight off the raw row keys.
        const totalColumns = [
            { field: 'Actual_Sales', header: 'Actual Sales' },
            ...(hasNC ? [{ field: 'NC', header: 'NC' }] : []),
            { field: 'Expected_Sales', header: 'Expected Sales' },
            { field: 'Cancelled', header: 'Cancelled' },
            ...(hasDiscount ? [{ field: 'Discount_Amount', header: 'Discount Amt' }] : []),
            { field: 'Total', header: 'Total' }
        ].map((col) => ({
            ...col,
            sortable: true,
            body: cellBodyTemplate(col.field),
            style: alignStyle('right', { minWidth: '130px' })
        }));

        return [dateColumn, ...modeColumns, ...totalColumns];
    };

    const columns = getColumns();

    // Hourly Sales (pivoted):
    // API format: { "DD-MM-YYYY": [ { time_range, Total_Hourly_Sales, payments: { CASH: 100, PAYTM: 50, ... } }, ... ], ... }
    // UI required:
    // - Column header: time_range
    // - Row header: payment modes (CASH, PAYTM, etc.)
    // - Row expand (accordion): date-wise sales for that payment mode
    const hourlyDates = hourlyData && typeof hourlyData === 'object' && !Array.isArray(hourlyData) ? Object.keys(hourlyData) : [];

    const hourlyTimeRanges = (() => {
        if (!hourlyDates.length) return [];
        const set = new Set();
        for (const d of hourlyDates) {
            const slots = Array.isArray(hourlyData?.[d]) ? hourlyData[d] : [];
            for (const s of slots) set.add(s?.time_range || '-');
        }
        const parseStartMinutes = (tr) => {
            const m = String(tr || '').match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?/i);
            if (!m) return Number.POSITIVE_INFINITY;
            let hh = parseInt(m[1], 10);
            const mm = parseInt(m[2] || '0', 10);
            const ap = (m[3] || '').toUpperCase();
            if (ap === 'PM' && hh < 12) hh += 12;
            if (ap === 'AM' && hh === 12) hh = 0;
            return hh * 60 + mm;
        };
        return Array.from(set).sort((a, b) => parseStartMinutes(a) - parseStartMinutes(b));
    })();

    const hourlyPaymentModes = (() => {
        if (!hourlyDates.length) return [];
        const set = new Set();
        for (const d of hourlyDates) {
            const slots = Array.isArray(hourlyData?.[d]) ? hourlyData[d] : [];
            for (const s of slots) {
                const p = s?.payments && typeof s.payments === 'object' && !Array.isArray(s.payments) ? s.payments : {};
                Object.keys(p).forEach((k) => set.add(k));
            }
        }
        return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
    })();

    const toNum = (v) => {
        const n = parseFloat(v ?? 0);
        return Number.isFinite(n) ? n : 0;
    };

    const hourlyPivotRows = (() => {
        if (!hourlyDates.length || !hourlyPaymentModes.length || !hourlyTimeRanges.length) return [];

        // mode -> time_range -> total
        const modeTotals = new Map();
        // mode -> date -> (time_range -> total)
        const modeByDate = new Map();

        for (const mode of hourlyPaymentModes) {
            modeTotals.set(mode, new Map(hourlyTimeRanges.map((tr) => [tr, 0])));
            modeByDate.set(mode, new Map(hourlyDates.map((d) => [d, new Map(hourlyTimeRanges.map((tr) => [tr, 0]))])));
        }

        for (const d of hourlyDates) {
            const slots = Array.isArray(hourlyData?.[d]) ? hourlyData[d] : [];
            for (const slot of slots) {
                const tr = slot?.time_range || '-';
                const payments = slot?.payments && typeof slot.payments === 'object' && !Array.isArray(slot.payments) ? slot.payments : {};
                for (const mode of hourlyPaymentModes) {
                    if (payments?.[mode] === undefined) continue;
                    const amt = toNum(payments[mode]);
                    const tMap = modeTotals.get(mode);
                    const dMap = modeByDate.get(mode);
                    if (tMap?.has(tr)) tMap.set(tr, (tMap.get(tr) || 0) + amt);
                    const perDate = dMap?.get(d);
                    if (perDate?.has(tr)) perDate.set(tr, (perDate.get(tr) || 0) + amt);
                }
            }
        }

        return hourlyPaymentModes.map((mode) => {
            const row = { payment_mode: mode };
            const tMap = modeTotals.get(mode) || new Map();
            let total = 0;
            hourlyTimeRanges.forEach((tr) => {
                const v = toNum(tMap.get(tr));
                row[tr] = v;
                total += v;
            });
            row.total = total;

            const dMap = modeByDate.get(mode) || new Map();
            const byDateRows = hourlyDates.map((d) => {
                const r = { date: d };
                const perDate = dMap.get(d) || new Map();
                let t = 0;
                hourlyTimeRanges.forEach((tr) => {
                    const v = toNum(perDate.get(tr));
                    r[tr] = v;
                    t += v;
                });
                r.total = t;
                return r;
            });
            row._byDate = byDateRows;
            return row;
        });
    })();

    // Search filter for the Hourly sales tab — same "check every field" approach as
    // filteredSalesData above, just applied inline since this array is already recomputed
    // fresh every render (no separate effect/state needed).
    const filteredHourlyPivotRows = !hourlyGlobalFilter
        ? hourlyPivotRows
        : hourlyPivotRows.filter((item) => {
              const searchTerm = hourlyGlobalFilter.toLowerCase();
              return Object.keys(item).some((key) => {
                  if (key === '_byDate') return false;
                  const value = item[key];
                  if (value === null || value === undefined) return false;
                  return String(value).toLowerCase().includes(searchTerm);
              });
          });

    // Shared header block (org name / period / branch / generated-on) every export uses.
    const exportFilterLines = () => [
        organizationName ? { text: organizationName, bold: true, size: 12, center: true, fill: 'FFEDEBFA' } : null,
        {
            left: fromDate && toDate ? `Period: ${moment(fromDate).format('DD-MM-YYYY')} to ${moment(toDate).format('DD-MM-YYYY')}` : '',
            right: `Branch: ${selectedBranch?.branch_name || selectedBranch || 'All Branches'}`,
            bold: true,
            fill: 'FFEDEBFA'
        },
        { right: `Generated On: ${moment().format('DD-MM-YYYY hh:mm A')}`, bold: true, fill: 'FFEDEBFA' }
    ];

    const buildSimplePdfDoc = (title, headers, tableBody) => {
        const doc = new jsPDF();
        let yPos = 15;
        const centerX = doc.internal.pageSize.getWidth() / 2;
        const rightX = doc.internal.pageSize.getWidth() - 14;
        if (organizationName) {
            doc.setFontSize(15);
            doc.setFont(undefined, 'bold');
            doc.text(organizationName, centerX, yPos, { align: 'center' });
            yPos += 8;
        }
        doc.setFontSize(13);
        doc.setFont(undefined, 'bold');
        doc.text(title, centerX, yPos, { align: 'center' });
        yPos += 8;
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        if (fromDate && toDate) {
            doc.text(`Period: ${moment(fromDate).format('DD-MM-YYYY')} to ${moment(toDate).format('DD-MM-YYYY')}`, 14, yPos);
        }
        doc.text(`Branch: ${selectedBranch?.branch_name || selectedBranch || 'All Branches'}`, rightX, yPos, { align: 'right' });
        yPos += 6;
        doc.text(`Generated On: ${moment().format('DD-MM-YYYY HH:mm')}`, rightX, yPos, { align: 'right' });
        yPos += 5;

        autoTable(doc, {
            startY: yPos + 5,
            head: [headers],
            body: tableBody,
            theme: 'grid',
            styles: { fontSize: 8, lineWidth: 0.1, lineColor: [180, 180, 180], halign: 'right' },
            columnStyles: { 0: { halign: 'left' } },
            headStyles: { fillColor: [0, 122, 255], textColor: 255, fontStyle: 'bold', halign: 'center' }
        });

        return doc;
    };

    const exportHourlyToExcel = async () => {
        if (filteredHourlyPivotRows.length === 0) return;
        const columnDefs = [
            { header: 'Payment Mode', getValue: (r) => r.payment_mode },
            ...hourlyTimeRanges.map((tr) => ({ header: tr, getValue: (r) => Number(r[tr] || 0), numeric: true })),
            { header: 'Total', getValue: (r) => Number(r.total || 0), numeric: true }
        ];
        await exportStyledExcel({
            fileName: `SalesReport_Hourly_${moment().format('YYYY-MM-DD')}.xlsx`,
            sheetName: 'Hourly Sales',
            title: 'Sales Reports - Hourly Sales',
            filterLines: exportFilterLines(),
            columns: columnDefs,
            rows: filteredHourlyPivotRows
        });
    };

    const exportHourlyToPDF = () => {
        if (filteredHourlyPivotRows.length === 0) return;
        const headers = ['Payment Mode', ...hourlyTimeRanges, 'Total'];
        const tableBody = filteredHourlyPivotRows.map((row) => [row.payment_mode, ...hourlyTimeRanges.map((tr) => `Rs.${formatCurrency(row[tr])}`), `Rs.${formatCurrency(row.total)}`]);
        buildSimplePdfDoc('Sales Reports - Hourly Sales', headers, tableBody).save(`SalesReport_Hourly_${moment().format('YYYY-MM-DD')}.pdf`);
    };

    const printHourlyReport = () => {
        if (filteredHourlyPivotRows.length === 0) return;
        const headers = ['Payment Mode', ...hourlyTimeRanges, 'Total'];
        const tableBody = filteredHourlyPivotRows.map((row) => [row.payment_mode, ...hourlyTimeRanges.map((tr) => `Rs.${formatCurrency(row[tr])}`), `Rs.${formatCurrency(row.total)}`]);
        const doc = buildSimplePdfDoc('Sales Reports - Hourly Sales', headers, tableBody);
        doc.autoPrint();
        window.open(doc.output('bloburl'), '_blank');
    };

    if (loading) {
        return (
            <div className="bill-reports-container">
                <Card>
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                        <ProgressSpinner />
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="bill-reports-container">
            {error && (
                <div
                    className="mb-3 p-3 border-round"
                    style={{
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        color: '#b91c1c'
                    }}
                >
                    <span>{error}</span>
                </div>
            )}
            {/* Sales Summary (cards + donut) is hidden — Branch Analysis's own Chart View covers this now. */}
            {SALES_SUMMARY_ENABLED && !isManagerView && Object.keys(summaryCards).length > 0 && (
                <ReportSummaryWithChart
                    summary={Object.fromEntries(Object.entries(summaryCards).filter(([key]) => !isHiddenTotalKey(key.replace(/^Total_/, ''))))}
                    fromDate={fromDate}
                    toDate={toDate}
                    selectedBranch={selectedBranch}
                    title="Sales Summary"
                    subtitle="Overview of sales by payment type"
                    getCardStyle={getSummaryCardStyle}
                    formatLabel={formatSummaryLabel}
                    isCurrency={(key) => !String(key || '').toLowerCase().includes('qty')}
                    excludeFromChart={excludeSalesTotalFromChart}
                    emphasizeKey={emphasizeSalesTotal}
                    alwaysShowKey={alwaysShowSalesField}
                    getTooltip={getSalesSummaryTooltip}
                />
            )}

            <Card className="mb-3">
                <div className="flex flex-wrap align-items-center gap-2 mb-3">
                    <h3 className="text-lg font-semibold m-0">Branch Analysis</h3>
                        {branchAnalysisRows.length > 0 && (
                            <Tag value={`${branchAnalysisRows.length} Branch${branchAnalysisRows.length > 1 ? 'es' : ''}`} severity="info" />
                        )}
                        <span className="text-color-secondary text-sm">
                            {branchAnalysisView === 'table' ? 'Hover Invoice No / Total Bills / Total Sales / Payment Split for a detailed breakdown' : 'Total Sales and Total Bills by branch'}
                        </span>
                        {branchAnalysisRows.length > 0 && (
                            <Button
                                label={branchAnalysisView === 'table' ? 'Chart View' : 'Table View'}
                                icon={branchAnalysisView === 'table' ? 'pi pi-chart-bar' : 'pi pi-table'}
                                className="p-button-outlined p-button-sm ml-auto"
                                onClick={() => setBranchAnalysisView((v) => (v === 'table' ? 'chart' : 'table'))}
                            />
                        )}
                    </div>

                    {branchAnalysisLoading ? (
                        <div className="flex justify-content-center align-items-center" style={{ minHeight: '150px' }}>
                            <ProgressSpinner style={{ width: 40, height: 40 }} />
                        </div>
                    ) : branchAnalysisRows.length === 0 ? (
                        <div className="flex flex-column align-items-center justify-content-center p-4">
                            <i className="pi pi-chart-bar text-4xl text-color-secondary mb-2"></i>
                            <p className="text-color-secondary m-0">No sales data found for the selected filters.</p>
                        </div>
                    ) : branchAnalysisView === 'chart' ? (
                        <>
                        {branchAnalysisRows.length > 1 ? (
                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <div className="text-sm font-semibold text-color-secondary mb-2 text-center">Total Sales by Branch</div>
                                    <div style={{ height: 240 }}>
                                        <Chart
                                            type="bar"
                                            data={{
                                                labels: branchAnalysisRows.map((r) => r.branchName),
                                                datasets: [{ data: branchAnalysisRows.map((r) => r.totalSales), backgroundColor: BRANCH_SALES_CHART_COLOR, borderRadius: 4, maxBarThickness: 24 }]
                                            }}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: {
                                                    legend: { display: false },
                                                    tooltip: { callbacks: { label: (ctx) => `₹${formatCurrency(ctx.parsed.y)}` } }
                                                },
                                                scales: {
                                                    x: { grid: { display: false }, ticks: { color: CHART_AXIS_TICK_COLOR } },
                                                    y: { beginAtZero: true, grid: { color: CHART_GRID_COLOR }, ticks: { color: CHART_AXIS_TICK_COLOR, callback: (v) => `₹${Number(v).toLocaleString('en-IN')}` } }
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="col-12 md:col-6">
                                    <div className="text-sm font-semibold text-color-secondary mb-2 text-center">Total Bills by Branch</div>
                                    <div style={{ height: 240 }}>
                                        <Chart
                                            type="bar"
                                            data={{
                                                labels: branchAnalysisRows.map((r) => r.branchName),
                                                datasets: [{ data: branchAnalysisRows.map((r) => r.totalBills), backgroundColor: BRANCH_BILLS_CHART_COLOR, borderRadius: 4, maxBarThickness: 24 }]
                                            }}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: {
                                                    legend: { display: false },
                                                    tooltip: { callbacks: { label: (ctx) => ctx.parsed.y.toLocaleString('en-IN') } }
                                                },
                                                scales: {
                                                    x: { grid: { display: false }, ticks: { color: CHART_AXIS_TICK_COLOR } },
                                                    y: { beginAtZero: true, grid: { color: CHART_GRID_COLOR }, ticks: { color: CHART_AXIS_TICK_COLOR } }
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            // A one-bar bar chart is the wrong form for one branch — a stat tile carries the same information more honestly.
                            <div className="grid">
                                <div className="col-6">
                                    <div className="text-center p-3">
                                        <div className="text-xs uppercase text-color-secondary font-semibold mb-1">Total Sales</div>
                                        <div className="text-3xl font-bold" style={{ color: BRANCH_SALES_CHART_COLOR }}>
                                            ₹{formatCurrency(branchAnalysisRows[0]?.totalSales)}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="text-center p-3">
                                        <div className="text-xs uppercase text-color-secondary font-semibold mb-1">Total Bills</div>
                                        <div className="text-3xl font-bold" style={{ color: BRANCH_BILLS_CHART_COLOR }}>
                                            {branchAnalysisRows[0]?.totalBills}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {(() => {
                            const usedModes = paymentModes.filter((mode) => branchAnalysisRows.some((r) => (r.modeAmounts?.[mode] || 0) > 0));
                            if (!usedModes.length) return null;
                            return (
                                <div className="mt-4">
                                    <div className="text-sm font-semibold text-color-secondary mb-2 text-center">Payment Split by Branch</div>
                                    <div style={{ height: 280 }}>
                                        <Chart
                                            type="bar"
                                            data={{
                                                labels: branchAnalysisRows.map((r) => r.branchName),
                                                datasets: usedModes.map((mode) => ({
                                                    label: mode,
                                                    data: branchAnalysisRows.map((r) => r.modeAmounts?.[mode] || 0),
                                                    backgroundColor: modeColor(mode),
                                                    borderColor: CHART_SURFACE_COLOR,
                                                    borderWidth: 2,
                                                    borderRadius: 4,
                                                    maxBarThickness: 24
                                                }))
                                            }}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: {
                                                    legend: {
                                                        position: 'bottom',
                                                        labels: { usePointStyle: true, pointStyle: 'circle', boxWidth: 8, boxHeight: 8, padding: 14, font: { size: 12 }, color: '#52514e' }
                                                    },
                                                    tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ₹${formatCurrency(ctx.parsed.y)}` } }
                                                },
                                                scales: {
                                                    x: { stacked: true, grid: { display: false }, ticks: { color: CHART_AXIS_TICK_COLOR } },
                                                    y: {
                                                        stacked: true,
                                                        beginAtZero: true,
                                                        grid: { color: CHART_GRID_COLOR },
                                                        ticks: { color: CHART_AXIS_TICK_COLOR, callback: (v) => `₹${Number(v).toLocaleString('en-IN')}` }
                                                    }
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            );
                        })()}
                        </>
                    ) : (
                        <DataTable
                            value={branchAnalysisRows}
                            dataKey="branchId"
                            showGridlines
                            className="p-datatable-sm report-datatable-compact"
                            tableStyle={{ minWidth: '40rem' }}
                            footerColumnGroup={
                                branchAnalysisRows.length > 1 ? (
                                    <ColumnGroup>
                                        <Row>
                                            <Column footer="Total" colSpan={2} style={footerCellStyle('center')} />
                                            <Column footer={String(branchAnalysisTotals.totalBills)} style={footerCellStyle('center')} />
                                            <Column footer={`₹${formatCurrency(branchAnalysisTotals.totalSales)}`} style={footerCellStyle('center', { isGrandTotal: true })} />
                                            <Column
                                                footer={
                                                    modeBreakdown(combinedModeRow).length ? (
                                                        <span className="branch-breakdown-trigger" onMouseEnter={showBranchBreakdown('split', combinedModeRow)} onMouseLeave={hideBranchBreakdown}>
                                                            <span className="branch-stat-value">{modeBreakdown(combinedModeRow).length} modes</span>
                                                            <i className="pi pi-chart-pie branch-breakdown-icon" />
                                                        </span>
                                                    ) : (
                                                        ''
                                                    )
                                                }
                                                style={footerCellStyle('center')}
                                            />
                                            <Column footer="" />
                                        </Row>
                                    </ColumnGroup>
                                ) : null
                            }
                        >
                            <Column field="branchName" header="Branch" style={alignStyle('center', { minWidth: '160px' })} headerStyle={headerAlignStyle({ textAlign: 'center' })} />
                            <Column
                                header="Invoice No"
                                style={alignStyle('center', { minWidth: '160px' })}
                                headerStyle={headerAlignStyle({ textAlign: 'center' })}
                                body={(row) =>
                                    row.invoiceStart || row.invoiceEnd ? (
                                        <span
                                            className={`branch-breakdown-trigger branch-invoice-range ${row.invoiceHasGap ? 'branch-invoice-gap' : 'branch-invoice-ok'}`}
                                            onMouseEnter={showBranchBreakdown('invoice', row)}
                                            onMouseLeave={hideBranchBreakdown}
                                        >
                                            <span className="branch-stat-value">
                                                {row.invoiceStart || '-'} — {row.invoiceEnd || '-'}
                                            </span>
                                            <i className={`pi ${row.invoiceHasGap ? 'pi-exclamation-triangle' : 'pi-check-circle'} branch-breakdown-icon`} />
                                        </span>
                                    ) : (
                                        '-'
                                    )
                                }
                            />
                            <Column
                                header="Total Bills"
                                style={alignStyle('center', { minWidth: '110px' })}
                                headerStyle={headerAlignStyle({ textAlign: 'center' })}
                                body={(row) => (
                                    <span className="branch-breakdown-trigger" onMouseEnter={showBranchBreakdown('bills', row)} onMouseLeave={hideBranchBreakdown}>
                                        <span className="branch-stat-value">{row.totalBills}</span>
                                        <i className="pi pi-chart-pie branch-breakdown-icon" />
                                    </span>
                                )}
                            />
                            <Column
                                header="Total Sales"
                                style={alignStyle('center', { minWidth: '140px' })}
                                headerStyle={headerAlignStyle({ textAlign: 'center' })}
                                body={(row) => (
                                    <span className="branch-breakdown-trigger" onMouseEnter={showBranchBreakdown('sales', row)} onMouseLeave={hideBranchBreakdown}>
                                        <span className="branch-stat-value branch-stat-sales">₹{formatCurrency(row.totalSales)}</span>
                                        <i className="pi pi-chart-pie branch-breakdown-icon" />
                                    </span>
                                )}
                            />
                            <Column
                                header="Payment Split"
                                style={alignStyle('center', { minWidth: '130px' })}
                                headerStyle={headerAlignStyle({ textAlign: 'center' })}
                                body={(row) =>
                                    modeBreakdown(row).length ? (
                                        <span className="branch-breakdown-trigger" onMouseEnter={showBranchBreakdown('split', row)} onMouseLeave={hideBranchBreakdown}>
                                            <span className="branch-stat-value">{modeBreakdown(row).length} modes</span>
                                            <i className="pi pi-chart-pie branch-breakdown-icon" />
                                        </span>
                                    ) : (
                                        '-'
                                    )
                                }
                            />
                            <Column
                                header="Avg Bill Value"
                                style={alignStyle('center', { minWidth: '120px' })}
                                headerStyle={headerAlignStyle({ textAlign: 'center' })}
                                body={(row) => `₹${formatCurrency(row.avgBillValue)}`}
                            />
                        </DataTable>
                    )}

                    {branchHover && branchHover.type === 'invoice' && (
                        <div
                            className={`branch-breakdown-popup ${branchHover.row.invoiceHasGap ? 'branch-invoice-popup-gap' : 'branch-invoice-popup-ok'}`}
                            style={{ top: branchHover.top, left: branchHover.left }}
                            onMouseEnter={() => setBranchHover(branchHover)}
                            onMouseLeave={hideBranchBreakdown}
                        >
                            <div className="breakdown-title">Invoice Sequence — {branchHover.row.branchName}</div>
                            {branchHover.row.invoiceHasGap ? (
                                <>
                                    <div className="breakdown-item breakdown-invoice-gap">
                                        <span className="breakdown-label">
                                            <span className="breakdown-dot" />
                                            Missing invoices
                                        </span>
                                        <span className="breakdown-value">{branchHover.row.invoiceMissingCount}</span>
                                    </div>
                                    <div className="breakdown-missing-list">
                                        {branchHover.row.invoiceMissingList.join(', ')}
                                        {branchHover.row.invoiceMissingCount > branchHover.row.invoiceMissingList.length && ', …'}
                                    </div>
                                </>
                            ) : (
                                <div className="breakdown-item breakdown-invoice-ok">
                                    <span className="breakdown-label">
                                        <span className="breakdown-dot" />
                                        Fully sequential — no gaps
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {branchHover && (branchHover.type === 'bills' || branchHover.type === 'sales') && (
                        <div
                            className="branch-breakdown-popup"
                            style={{ top: branchHover.top, left: branchHover.left }}
                            onMouseEnter={() => setBranchHover(branchHover)}
                            onMouseLeave={hideBranchBreakdown}
                        >
                            <div className="breakdown-title">
                                {branchHover.type === 'bills' ? 'Bills Breakdown' : 'Sales Breakdown'} — {branchHover.row.branchName}
                            </div>
                            {(() => {
                                const categories = breakdownCategories(branchHover.row, branchHover.type === 'bills' ? (count) => count : (_count, amount) => amount);
                                if (!categories.length) {
                                    return <div className="breakdown-empty">No Cancelled / NC / Credit / Discount bills — every bill is a clean sale.</div>;
                                }
                                return categories.map((cat) => (
                                    <div key={cat.key} className={`breakdown-item breakdown-${cat.key}`}>
                                        <span className="breakdown-label">
                                            <span className="breakdown-dot" />
                                            {cat.label}
                                        </span>
                                        <span className="breakdown-value">{branchHover.type === 'bills' ? cat.value : `₹${formatCurrency(cat.value)}`}</span>
                                    </div>
                                ));
                            })()}
                        </div>
                    )}

                    {branchHover && branchHover.type === 'split' && (
                        <div
                            className="branch-breakdown-popup"
                            style={{ top: branchHover.top, left: branchHover.left }}
                            onMouseEnter={() => setBranchHover(branchHover)}
                            onMouseLeave={hideBranchBreakdown}
                        >
                            <div className="breakdown-title">Payment Split — {branchHover.row.branchName}</div>
                            {modeBreakdown(branchHover.row).map(([mode, amount]) => (
                                <div key={mode} className="breakdown-item">
                                    <span className="breakdown-label">
                                        <span className="breakdown-dot" style={{ background: modeColor(mode) }} />
                                        {mode}
                                    </span>
                                    <span className="breakdown-value">₹{formatCurrency(amount)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>

            {/* Data Table Section - Tabs */}
            <Card>
                <div className="flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
                    <h3 className="text-xl font-semibold m-0">Report Data</h3>
                </div>
                <div className="report-data-tabs">
                    <TabView activeIndex={activeReportTab} onTabChange={(e) => setActiveReportTab(e.index)}>
                        <TabPanel header="Sales by date range">
                            <div className="flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                                <div className="flex align-items-center gap-2">{filteredSalesData.length > 0 && <Tag value={`${filteredSalesData.length} Records`} severity="info" />}</div>
                                <div className="flex flex-wrap gap-2">
                                    <span className="p-input-icon-left w-full sm:w-auto" style={{ minWidth: '250px' }}>
                                        <i className="pi pi-search"></i>
                                        <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Search sales..." className="w-full" />
                                    </span>
                                    {canExport && (
                                        <div className="flex align-items-center gap-2 bill-export-actions">
                                            <Button label="Excel" icon="pi pi-file-excel" className="p-button-success p-button-sm" onClick={exportToExcel} disabled={filteredSalesData.length === 0} />
                                            <Button label="PDF" icon="pi pi-file-pdf" className="p-button-danger p-button-sm" onClick={exportToPDF} disabled={filteredSalesData.length === 0} />
                                            <Button label="Print" icon="pi pi-print" className="p-button-secondary p-button-sm" onClick={printReport} disabled={filteredSalesData.length === 0} />
                                        </div>
                                    )}
                                </div>
                            </div>
                            {loading ? (
                                <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                                    <ProgressSpinner />
                                </div>
                            ) : filteredSalesData.length === 0 ? (
                                <div className="flex flex-column align-items-center justify-content-center p-6">
                                    <i className="pi pi-inbox text-6xl text-color-secondary mb-3"></i>
                                    <p className="text-xl text-color-secondary">No data available</p>
                                    <p className="text-color-secondary mt-2">
                                        {salesData.length === 0 ? (fromDate && toDate ? 'No sales data found for the selected filters' : 'Please apply filters to view sales reports') : 'No records match your search criteria'}
                                    </p>
                                </div>
                            ) : (
                                <>
                                <Tooltip key={`sales-modes-${filteredSalesData.length}`} target=".payment-mode-cell" />
                                <DataTable
                                    value={filteredSalesData}
                                    showGridlines
                                    paginator
                                    rows={25}
                                    stripedRows
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    currentPageReportTemplate="{first} to {last} of {totalRecords} records"
                                    tableStyle={{ minWidth: '50rem' }}
                                    className="p-datatable-sm report-datatable-compact"
                                    emptyMessage="No sales data found"
                                    footerColumnGroup={
                                        !isManagerView && Object.keys(summary).length > 0 && columns.length > 0 ? (
                                            <ColumnGroup>
                                                <Row>
                                                    {columns.map((col, index) => {
                                                        const isQty = col.field.toLowerCase().includes('qty');
                                                        const value = summary[`Total_${col.field}`] ?? 0;
                                                        const footerContent = index === 0 ? 'Grand Total:' : isQty ? String(value) : `₹${formatCurrency(value)}`;
                                                        return <Column key={col.field} footer={footerContent} style={footerCellStyle('center', { isGrandTotal: index === 0 })} />;
                                                    })}
                                                </Row>
                                            </ColumnGroup>
                                        ) : null
                                    }
                                >
                                    {columns.map((col) => (
                                        <Column key={col.field} field={col.field} header={col.header} sortable={col.sortable} body={col.body} style={col.style} headerStyle={headerAlignStyle(col.style)} />
                                    ))}
                                </DataTable>
                                </>
                            )}
                        </TabPanel>
                        <TabPanel header="Hourly sales">
                            <div className="flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                                <div className="flex align-items-center gap-2">{filteredHourlyPivotRows.length > 0 && <Tag value={`${filteredHourlyPivotRows.length} Records`} severity="info" />}</div>
                                <div className="flex flex-wrap gap-2">
                                    <span className="p-input-icon-left w-full sm:w-auto" style={{ minWidth: '250px' }}>
                                        <i className="pi pi-search"></i>
                                        <InputText value={hourlyGlobalFilter} onChange={(e) => setHourlyGlobalFilter(e.target.value)} placeholder="Search sales..." className="w-full" />
                                    </span>
                                    {canExport && (
                                        <div className="flex align-items-center gap-2 bill-export-actions">
                                            <Button label="Excel" icon="pi pi-file-excel" className="p-button-success p-button-sm" onClick={exportHourlyToExcel} disabled={filteredHourlyPivotRows.length === 0} />
                                            <Button label="PDF" icon="pi pi-file-pdf" className="p-button-danger p-button-sm" onClick={exportHourlyToPDF} disabled={filteredHourlyPivotRows.length === 0} />
                                            <Button label="Print" icon="pi pi-print" className="p-button-secondary p-button-sm" onClick={printHourlyReport} disabled={filteredHourlyPivotRows.length === 0} />
                                        </div>
                                    )}
                                </div>
                            </div>
                            {loadingHourly ? (
                                <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                                    <ProgressSpinner />
                                </div>
                            ) : filteredHourlyPivotRows.length === 0 ? (
                                <div className="flex flex-column align-items-center justify-content-center p-6">
                                    <i className="pi pi-clock text-6xl text-color-secondary mb-3"></i>
                                    <p className="text-xl text-color-secondary">No hourly data</p>
                                    <p className="text-color-secondary mt-2">
                                        {hourlyPivotRows.length === 0 ? 'Data loads when you open this tab with date range and branch selected.' : 'No records match your search criteria'}
                                    </p>
                                </div>
                            ) : (
                                <DataTable
                                    value={filteredHourlyPivotRows}
                                    dataKey="payment_mode"
                                    showGridlines
                                    paginator
                                    rows={25}
                                    stripedRows
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    tableStyle={{ minWidth: '60rem' }}
                                    className="p-datatable-sm report-datatable-compact"
                                    emptyMessage="No hourly sales"
                                    expandedRows={expandedHourlyRows}
                                    onRowToggle={(e) => setExpandedHourlyRows(e.data)}
                                    rowExpansionTemplate={(row) => (
                                        <div className="p-3">
                                            <strong className="block mb-2">Date wise sales</strong>
                                            <DataTable value={row._byDate || []} showGridlines className="p-datatable-sm report-datatable-compact" tableStyle={{ minWidth: '50rem' }} emptyMessage="No date-wise data">
                                                <Column field="date" header="Date" sortable body={(r) => formatDate(r.date)} style={{ minWidth: '110px' }} />
                                                {hourlyTimeRanges.map((tr) => (
                                                    <Column key={tr} field={tr} header={tr} body={(r) => `₹${formatCurrency(r[tr])}`} style={{ textAlign: 'right', minWidth: '120px' }} />
                                                ))}
                                                <Column field="total" header="Total" body={(r) => `₹${formatCurrency(r.total)}`} style={{ textAlign: 'right', minWidth: '120px', fontWeight: 600 }} />
                                            </DataTable>
                                        </div>
                                    )}
                                >
                                    <Column expander style={{ width: '3rem' }} />
                                    <Column field="payment_mode" header="Payment Mode" sortable style={{ minWidth: '140px' }} />
                                    {hourlyTimeRanges.map((tr) => (
                                        <Column key={tr} field={tr} header={tr} body={(row) => `₹${formatCurrency(row[tr])}`} style={{ textAlign: 'right', minWidth: '120px' }} />
                                    ))}
                                    <Column field="total" header="Total" sortable body={(row) => `₹${formatCurrency(row.total)}`} style={{ textAlign: 'right', minWidth: '140px', fontWeight: 700 }} />
                                </DataTable>
                            )}
                        </TabPanel>
                    </TabView>
                </div>
            </Card>
        </div>
    );
};

export default SalesReportsContent;
